import React, { Component } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Button,
  Container,
  Input,
  FormGroup,
  Label,
  Alert,
} from "reactstrap";
import {
  getTransportSettings,
  updateTransportSettings,
} from "../../Endpoint/Admin/index";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import { Link } from "react-router-dom";

class TransportSettings extends Component {
  constructor(props) {
    super(props);
    this.state = {
      settings: null,
      isLoaded: false,
      isSaving: false,
      hasChanges: false,
      formData: {
        nightStartHour: 20,
        nightEndHour: 6,
        defaultDayRateMultiplier: 1.0,
        defaultNightRateMultiplier: 1.5,
        defaultDriverAllowancePerNight: 50,
        minAdvanceBookingHours: 2,
        defaultMinimumKm: 5,
        defaultMinimumCharge: 10,
        defaultCurrency: "USD",
      },
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Transport", link: "/transport/requests" },
        { title: "Settings", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadSettings();
  }

  loadSettings = () => {
    getTransportSettings()
      .then((result) => {
        if (result && result.status === 200 && result.data) {
          const data = result.data;
          this.setState({
            settings: data,
            formData: {
              nightStartHour: data.nightStartHour || 20,
              nightEndHour: data.nightEndHour || 6,
              defaultDayRateMultiplier: data.defaultDayRateMultiplier || 1.0,
              defaultNightRateMultiplier: data.defaultNightRateMultiplier || 1.5,
              defaultDriverAllowancePerNight: data.defaultDriverAllowancePerNight || 50,
              minAdvanceBookingHours: data.minAdvanceBookingHours || 2,
              defaultMinimumKm: data.defaultMinimumKm || 5,
              defaultMinimumCharge: data.defaultMinimumCharge || 10,
              defaultCurrency: data.defaultCurrency || "USD",
            },
            isLoaded: true,
            hasChanges: false,
          });
        } else {
          this.setState({ isLoaded: true });
          toast.info("No settings found. Default values loaded.");
        }
      })
      .catch(() => {
        this.setState({ isLoaded: true });
        toast.error("Error loading settings");
      });
  };

  handleChange = (field, value) => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [field]: value,
      },
      hasChanges: true,
    }));
  };

  handleSave = () => {
    const { formData } = this.state;

    // Validation
    if (formData.nightStartHour < 0 || formData.nightStartHour > 23) {
      toast.error("Night start hour must be between 0 and 23");
      return;
    }
    if (formData.nightEndHour < 0 || formData.nightEndHour > 23) {
      toast.error("Night end hour must be between 0 and 23");
      return;
    }
    if (formData.defaultNightRateMultiplier < 0.1 || formData.defaultNightRateMultiplier > 10) {
      toast.error("Night rate multiplier must be between 0.1 and 10");
      return;
    }
    if (formData.defaultDayRateMultiplier < 0.1 || formData.defaultDayRateMultiplier > 10) {
      toast.error("Day rate multiplier must be between 0.1 and 10");
      return;
    }

    this.setState({ isSaving: true });

    updateTransportSettings(formData)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Settings saved successfully");
          this.setState({ hasChanges: false, settings: response.data });
        } else {
          toast.error(response?.message || "Failed to save settings");
        }
        this.setState({ isSaving: false });
      })
      .catch(() => {
        toast.error("Error saving settings");
        this.setState({ isSaving: false });
      });
  };

  formatHour = (hour) => {
    if (hour === 0) return "12:00 AM (Midnight)";
    if (hour === 12) return "12:00 PM (Noon)";
    if (hour < 12) return `${hour}:00 AM`;
    return `${hour - 12}:00 PM`;
  };

  render() {
    const { isLoaded, isSaving, hasChanges, formData } = this.state;

    const hourOptions = [];
    for (let i = 0; i < 24; i++) {
      hourOptions.push(
        <option key={i} value={i}>
          {this.formatHour(i)}
        </option>
      );
    }

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Transport Settings" breadcrumbItems={this.state.breadcrumbItems} />

            <Row className="mb-4">
              <Col>
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0">
                    <i className="ri-settings-3-line mr-2"></i>
                    Transport Configuration
                  </h4>
                  <Link to="/transport/requests">
                    <Button color="secondary" outline>
                      <i className="ri-arrow-left-line mr-1"></i> Back
                    </Button>
                  </Link>
                </div>
              </Col>
            </Row>

            {hasChanges && (
              <Alert color="warning" className="mb-4">
                <i className="ri-alert-line mr-2"></i>
                You have unsaved changes. Don't forget to save!
              </Alert>
            )}

            {!isLoaded ? (
              <Card>
                <CardBody className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="mt-3">Loading settings...</p>
                </CardBody>
              </Card>
            ) : (
              <Row>
                {/* Driver Allowance */}
                <Col lg={6}>
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="mb-4">
                        <i className="ri-user-star-line mr-2 text-warning"></i>
                        Driver Allowance
                      </h5>
                      <p className="text-muted mb-4">
                        Overnight allowance paid to drivers for multi-day outstation trips.
                      </p>

                      <FormGroup>
                        <Label>Allowance Per Night ($)</Label>
                        <Input
                          type="number"
                          value={formData.defaultDriverAllowancePerNight}
                          onChange={(e) => this.handleChange("defaultDriverAllowancePerNight", parseFloat(e.target.value) || 0)}
                          step="5"
                          min="0"
                        />
                        <small className="text-muted">
                          Added to the trip cost for each overnight stay
                        </small>
                      </FormGroup>

                      <Alert color="light" className="border mb-0">
                        <strong>Example:</strong> A 3-day trip will add ${formData.defaultDriverAllowancePerNight * 2} for driver allowance (2 nights)
                      </Alert>
                    </CardBody>
                  </Card>
                </Col>

                {/* Minimum Requirements */}
                <Col lg={6}>
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="mb-4">
                        <i className="ri-ruler-line mr-2 text-info"></i>
                        Minimum Requirements
                      </h5>
                      <p className="text-muted mb-4">
                        Default minimum values for per-km services (intercity/outstation).
                      </p>

                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Minimum Distance (KM)</Label>
                            <Input
                              type="number"
                              value={formData.defaultMinimumKm}
                              onChange={(e) => this.handleChange("defaultMinimumKm", parseInt(e.target.value) || 0)}
                              min="0"
                            />
                            <small className="text-muted">Minimum KM charged</small>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Minimum Charge ($)</Label>
                            <Input
                              type="number"
                              value={formData.defaultMinimumCharge}
                              onChange={(e) => this.handleChange("defaultMinimumCharge", parseFloat(e.target.value) || 0)}
                              step="5"
                              min="0"
                            />
                            <small className="text-muted">Minimum trip charge</small>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Advance Booking (Hours)</Label>
                            <Input
                              type="number"
                              value={formData.minAdvanceBookingHours}
                              onChange={(e) => this.handleChange("minAdvanceBookingHours", parseInt(e.target.value) || 0)}
                              min="0"
                            />
                            <small className="text-muted">Minimum hours before trip</small>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Default Currency</Label>
                            <Input
                              type="select"
                              value={formData.defaultCurrency}
                              onChange={(e) => this.handleChange("defaultCurrency", e.target.value)}
                            >
                              <option value="USD">USD ($)</option>
                              <option value="EUR">EUR (&euro;)</option>
                              <option value="GBP">GBP (&pound;)</option>
                              <option value="CDF">CDF (FC)</option>
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Save Button */}
            <Row>
              <Col>
                <Card>
                  <CardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <i className="ri-information-line mr-2 text-muted"></i>
                      <span className="text-muted">
                        These settings apply as defaults. Individual services can override them.
                      </span>
                    </div>
                    <Button
                      color="primary"
                      size="lg"
                      onClick={this.handleSave}
                      disabled={isSaving || !hasChanges}
                    >
                      {isSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="ri-save-line mr-2"></i>
                          Save Settings
                        </>
                      )}
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>

        <ToastContainer />
      </React.Fragment>
    );
  }
}

export default TransportSettings;
